import React, { useState, useEffect, useCallback } from 'react';
import copywritingService, {
  CopyType,
  Seniority,
  CopyAnalysis,
} from '../services/copywritingService';
import gtmService, { Icp, Persona, Offer } from '../services/gtmService';
import MarkdownView from '../components/MarkdownView';
import EditableMarkdown from '../components/EditableMarkdown';
import SavedAiDocuments from '../components/SavedAiDocuments';
import aiDocumentService from '../services/aiDocumentService';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';

type Mode = 'generate' | 'analyze';

const TYPE_OPTIONS: { value: CopyType; label: string }[] = [
  { value: 'first_touch', label: 'First Touch' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'sequence', label: '3er-Sequenz' },
  { value: 'cta', label: 'CTA-Ideen' },
];

const SENIORITY_OPTIONS: { value: Seniority; label: string }[] = [
  { value: 'ic', label: 'Individual Contributor' },
  { value: 'manager', label: 'Manager' },
  { value: 'vp', label: 'VP' },
];

const scoreColor = (n: number): string => (n >= 8 ? 'var(--sec-success)' : n >= 6 ? 'var(--sec-warning)' : 'var(--sec-danger)');

const CopywritingStudioPage: React.FC = () => {
  const [mode, setMode] = useState<Mode>('generate');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // GTM context options
  const [icps, setIcps] = useState<Icp[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);

  // generate form
  const [type, setType] = useState<CopyType>('first_touch');
  const [form, setForm] = useState({
    offerId: '',
    icpId: '',
    personaId: '',
    product: '',
    targetPersona: '',
    seniority: 'manager' as Seniority,
    angle: '',
    trigger: '',
    tone: '',
    followUpNumber: 2,
    previousEmail: '',
  });
  const [generated, setGenerated] = useState('');
  const [generatedDocId, setGeneratedDocId] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  // analyze
  const [emailText, setEmailText] = useState('');
  const [analyzeSeniority, setAnalyzeSeniority] = useState<Seniority | ''>('');
  const [analysis, setAnalysis] = useState<CopyAnalysis | null>(null);
  const [analysisRaw, setAnalysisRaw] = useState('');

  const loadGtm = useCallback(async () => {
    try {
      const [i, p, o] = await Promise.all([
        gtmService.getIcps(),
        gtmService.getPersonas(),
        gtmService.getOffers(),
      ]);
      setIcps(i);
      setPersonas(p);
      setOffers(o);
    } catch {
      // GTM context is optional — silently ignore load errors here.
    }
  }, []);

  useEffect(() => {
    loadGtm();
  }, [loadGtm]);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError('');
      setGenerated('');
      const result = await copywritingService.generate({
        type,
        offerId: form.offerId || undefined,
        icpId: form.icpId || undefined,
        personaId: form.personaId || undefined,
        product: form.product || undefined,
        targetPersona: form.targetPersona || undefined,
        seniority: type === 'sequence' ? form.seniority : undefined,
        angle: form.angle || undefined,
        trigger: form.trigger || undefined,
        tone: form.tone || undefined,
        followUpNumber: type === 'follow_up' ? form.followUpNumber : undefined,
        previousEmail: type === 'follow_up' ? form.previousEmail || undefined : undefined,
      });
      setGenerated(result.content);
      setGeneratedDocId(result.documentId);
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Generieren');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async (text?: string) => {
    const target = text ?? emailText;
    if (!target.trim()) {
      setError('Bitte E-Mail-Text eingeben');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setAnalysis(null);
      setAnalysisRaw('');
      const result = await copywritingService.analyze(
        target,
        (analyzeSeniority || undefined) as Seniority | undefined
      );
      if (result.analysis) setAnalysis(result.analysis);
      else setAnalysisRaw(result.raw || 'Keine strukturierte Analyse erhalten.');
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der Analyse');
    } finally {
      setLoading(false);
    }
  };

  const sendToAnalyzer = () => {
    setMode('analyze');
    setEmailText(generated);
    setAnalysis(null);
    setAnalysisRaw('');
  };

  return (
    <div className="page-container">
      <PageHeader title="Kaltakquise-Texte (Cold-Copy Studio)" subtitle={routeById('copywriting-studio')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Research-basierte Cold-Copy (powered by den Skills <em>copywriting-first-touch</em>,{' '}
        <em>copywriting-follow-up</em>, <em>copywriting-*-sequence</em>, <em>cta-designer</em>) mit{' '}
        <em>copywriting-analyzer</em> als Qualitäts-Gate. Optional auf ICP/Persona/Angebot aus dem
        GTM-Setup geerdet.
      </p>

      <div className="btn-group" style={{ marginBottom: '24px' }}>
        <button
          className={`btn ${mode === 'generate' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setMode('generate')}
        >
          Generieren
        </button>
        <button
          className={`btn ${mode === 'analyze' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setMode('analyze')}
        >
          Analysieren & Scoren
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {mode === 'generate' && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <div className="form-group">
            <label className="form-label">Typ</label>
            <select className="form-input" value={type} onChange={(e) => setType(e.target.value as CopyType)}>
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <div className="form-group" style={{ flex: '1 1 200px' }}>
              <label className="form-label">Angebot (GTM)</label>
              <select className="form-input" value={form.offerId} onChange={(e) => setForm({ ...form, offerId: e.target.value })}>
                <option value="">— keins —</option>
                {offers.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px' }}>
              <label className="form-label">ICP (GTM)</label>
              <select className="form-input" value={form.icpId} onChange={(e) => setForm({ ...form, icpId: e.target.value })}>
                <option value="">— keiner —</option>
                {icps.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px' }}>
              <label className="form-label">Persona (GTM)</label>
              <select className="form-input" value={form.personaId} onChange={(e) => setForm({ ...form, personaId: e.target.value })}>
                <option value="">— keine —</option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <div className="form-group" style={{ flex: '1 1 240px' }}>
              <label className="form-label">Produkt/Angebot (Freitext, falls kein GTM)</label>
              <input className="form-input" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} />
            </div>
            <div className="form-group" style={{ flex: '1 1 240px' }}>
              <label className="form-label">Zielperson (Freitext)</label>
              <input className="form-input" value={form.targetPersona} onChange={(e) => setForm({ ...form, targetPersona: e.target.value })} />
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <div className="form-group" style={{ flex: '1 1 240px' }}>
              <label className="form-label">Trigger / Signal (optional)</label>
              <input className="form-input" value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} placeholder="z.B. frische Finanzierung, neuer VP, Tool-Wechsel" />
            </div>
            <div className="form-group" style={{ flex: '1 1 240px' }}>
              <label className="form-label">Angle (optional)</label>
              <input className="form-input" value={form.angle} onChange={(e) => setForm({ ...form, angle: e.target.value })} />
            </div>
          </div>

          {type === 'sequence' && (
            <div className="form-group">
              <label className="form-label">Zielebene (Seniorität)</label>
              <select className="form-input" value={form.seniority} onChange={(e) => setForm({ ...form, seniority: e.target.value as Seniority })}>
                {SENIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {type === 'follow_up' && (
            <>
              <div className="form-group">
                <label className="form-label">Follow-up Nr.</label>
                <input
                  type="number"
                  min={2}
                  className="form-input"
                  value={form.followUpNumber}
                  onChange={(e) => setForm({ ...form, followUpNumber: Number(e.target.value) })}
                  style={{ width: '120px' }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Vorherige E-Mail (optional)</label>
                <textarea
                  className="form-textarea"
                  rows={4}
                  value={form.previousEmail}
                  onChange={(e) => setForm({ ...form, previousEmail: e.target.value })}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Tonalität (optional)</label>
            <input className="form-input" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} placeholder="direkt, menschlich, selbstbewusst" />
          </div>

          <button className="btn btn-success" onClick={handleGenerate} disabled={loading}>
            {loading ? 'Generiere…' : 'Copy generieren'}
          </button>

          {generated && (
            <div style={{ marginTop: '20px' }}>
              <EditableMarkdown
                title="Generierte Copy"
                content={generated}
                onSave={
                  generatedDocId
                    ? async (c) => {
                        await aiDocumentService.update(generatedDocId, { content: c });
                        setGenerated(c);
                        setReloadKey((k) => k + 1);
                      }
                    : undefined
                }
              />
              <button className="btn btn-primary btn-sm" style={{ marginTop: '8px' }} onClick={sendToAnalyzer}>
                Scoren →
              </button>
            </div>
          )}

          <SavedAiDocuments feature="copywriting" reloadKey={reloadKey} />
        </div>
      )}

      {mode === 'analyze' && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <div className="form-group">
            <label className="form-label">E-Mail-Text</label>
            <textarea
              className="form-textarea"
              rows={10}
              value={emailText}
              onChange={(e) => setEmailText(e.target.value)}
              placeholder="Cold-E-Mail hier einfügen…"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Zielebene (optional)</label>
            <select className="form-input" value={analyzeSeniority} onChange={(e) => setAnalyzeSeniority(e.target.value as Seniority | '')} style={{ width: '240px' }}>
              <option value="">— egal —</option>
              {SENIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-success" onClick={() => handleAnalyze()} disabled={loading}>
            {loading ? 'Analysiere…' : 'Scoren & verbessern'}
          </button>

          {analysisRaw && <MarkdownView content={analysisRaw} style={{ marginTop: '16px' }} />}

          {analysis && (
            <div style={{ marginTop: '20px' }}>
              <div className="card" style={{ marginBottom: '12px' }}>
                <div className="card-header">
                  <div className="card-title">
                    Gesamt-Score:{' '}
                    <span style={{ color: scoreColor(analysis.overall) }}>{analysis.overall}/10</span>
                  </div>
                  {analysis.penalties ? (
                    <span style={{ color: 'var(--sec-danger)', fontWeight: 600 }}>Penalties: −{Math.abs(analysis.penalties)}</span>
                  ) : null}
                </div>
                {analysis.verdict && (
                  <div style={{ fontSize: '14px', color: 'var(--sec-fg-muted)', marginTop: '8px' }}>{analysis.verdict}</div>
                )}
              </div>

              <div className="card" style={{ marginBottom: '12px' }}>
                <div className="card-title" style={{ marginBottom: '8px' }}>Dimensionen</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px' }}>
                  {Object.entries(analysis.scores || {}).map(([k, v]) => (
                    <div key={k} style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{k}</span>
                      <strong style={{ color: scoreColor(v) }}>{v}/10</strong>
                    </div>
                  ))}
                </div>
              </div>

              {analysis.redFlags?.length > 0 && (
                <div className="alert alert-error" style={{ marginBottom: '12px' }}>
                  <strong>Red Flags:</strong>
                  <ul style={{ margin: '4px 0 0 18px' }}>
                    {analysis.redFlags.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.issues?.length > 0 && (
                <div className="card" style={{ marginBottom: '12px' }}>
                  <div className="card-title" style={{ marginBottom: '8px' }}>Verbesserungen</div>
                  <ul style={{ margin: '0 0 0 18px', fontSize: '13px', color: 'var(--sec-fg-muted)' }}>
                    {analysis.issues.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.rewrite && (
                <div className="card">
                  <div className="card-header">
                    <div className="card-title">Verbesserte Version</div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() =>
                        navigator.clipboard?.writeText(
                          `${analysis.rewrite.subject}\n\n${analysis.rewrite.body}`
                        )
                      }
                    >
                      Kopieren
                    </button>
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--sec-fg-subtle)' }}>BETREFF</div>
                    <div style={{ fontSize: '14px', color: 'var(--sec-fg)', marginBottom: '8px' }}>{analysis.rewrite.subject}</div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--sec-fg-subtle)' }}>BODY</div>
                    <MarkdownView content={analysis.rewrite.body} />
                  </div>
                </div>
              )}
            </div>
          )}

          <SavedAiDocuments feature="copywriting" reloadKey={reloadKey} />
        </div>
      )}
    </div>
  );
};

export default CopywritingStudioPage;
